// The bank of the trust form of the client application (SEP-293).
//
// THE SOURCE IS THE FIRM'S OWN FORM: `DGW Trust App Form v1.1 0925`, the form
// of a trust account. The firm uses the same form for a charity. The text is
// `docs/sources/sep-293-app-trust.txt`, and the question list is
// `docs/sources/sep-293-trust-questions.md`. The rules H1 to H17 of the
// design apply on top of the question list.
//
// # ONE FORM FOR EACH SERVICE
//
// The paper form prints the three services. Parts 4, 8 and 9 (the financial
// status of the trust, the objectives and the experience, and the risk
// questions) "do not need to be completed" for the Execution Only service, so
// each question of those parts shows only for the Discretionary Managed
// service and the Advisory service. The service type does not select another
// trust form.
//
// # THE APPLICANT IS THE NOMINATED REPRESENTATIVE
//
// The person who fills in the form is the nominated representative of the
// trust (Part 2). The representative answers under the ids of the individual
// form (rule H1), with the address history of the individual form 3.0, as on
// the company forms. The trust form of the representative also asks a home
// number and a business number, so the applicant section asks them.
//
// The other persons of Part 3 (the settlor, the beneficiaries, the trustees,
// the protector, and each person with an interest of 25% or more) are a
// person group, `trust_party_<n>_*`, of seven blocks. Each block follows the
// paper form: a yes or no question about 3 years at the current address, one
// previous address and one telephone number.
//
// # A CHARITY
//
// The form names a charity once only. The type of the trust tells a charity
// apart: "Charitable trust" and "Registered charity" open the charity number
// and three rows of Part 4 that the entity fact find holds (donations and
// grants, distributions, administrative expenses). This is the default of
// open question Q19 of the design. Each other word of the form stays.
//
// # WHAT IS NOT HERE
//
//   - The signatures (Part 15, and the signature column of the authorised
//     signatories). The submission act replaces the signature of the
//     representative. The firm gets the signatures of the other trustees
//     off-line.
//   - Appendix I and Appendix II, the lists of documents. The investment
//     manager asks for the documents after the submission (rule H14).
//   - Declaration 6c, "Schedule of Charges" (rule H8).
//
// # THE WORDING IS DRAFT
//
// Each label of the paper form is the firm's draft copy. The online questions
// that the paper form does not print (the yes or no questions that open a
// person, an address or a row, the list of trust types, and the charity rows)
// are our own copy, and the firm settles them before the hold of the feature
// lifts.
//
// # THE IDS ARE FROZEN
//
// `FROZEN_CLIENT_APPLICATION_IDS_BY_FORM` in `client-application-frozen-ids.ts`
// pins the ids of the bank. A slice may add an id, and it may never drop or
// rename one.

import {
    addressQuestions,
    individualSectionQuestions,
    personGroup,
    questionChanges,
    sharedServiceTypeSection,
} from './client-application-blocks';
import {
    ENTITY_BANK_RULES,
    authorisedSignatoryQuestions,
    entityAccountUsageQuestions,
    entityAdditionalNotesSection,
    entityApplicantQuestions,
    entityBankAccountQuestions,
    entityDeclarationQuestions,
    entityDeclarationsSection,
    entityHealthSection,
    entityNationalClientIdentifierExplainer,
    entityObjectivesQuestions,
    entityPortalSecurityQuestions,
    entityRiskQuestions,
    entityThirdPartyQuestions,
} from './client-application-form-company';
import type {
    ClientApplicationBank,
    ClientApplicationCondition,
    ClientApplicationQuestion,
    ClientApplicationSection,
    ClientApplicationStatement,
} from './client-application-questions';

/** The version of the trust form, from the footer of the paper form. */
export const TRUST_FORM_VERSIONS = ['1.1'] as const;

/** The number of person blocks of Part 3. */
export const TRUST_PARTY_COUNT = 7;
/** The number of rows of the table of authorised signatories. */
export const TRUST_AUTHORISED_SIGNATORY_COUNT = 8;
/** The id prefix of the persons connected to the trust (Part 3). */
export const TRUST_PARTY_PREFIX = 'trust_party';

/** The types of trust. The paper form asks the type as free text (open question Q18). */
export const TRUST_TYPE_OPTIONS = [
    'Bare trust',
    'Discretionary trust',
    'Interest in possession trust',
    'Charitable trust',
    'Registered charity',
    'Other',
] as const;

/** The types of trust that a charity chooses (open question Q19). */
export const TRUST_CHARITY_TYPES: readonly string[] = ['Charitable trust', 'Registered charity'];

/**
 * The condition of Parts 4, 8 and 9: the services of an investment manager.
 * The Execution Only service skips those parts.
 */
const MANAGED_SERVICES: ClientApplicationCondition = {
    questionId: 'service_type',
    equals: ['Discretionary Managed service', 'Advisory service'],
};

/** The condition of the charity rows. */
const CHARITY: ClientApplicationCondition = {
    questionId: 'trust_type',
    equals: TRUST_CHARITY_TYPES,
};

/**
 * One question that shows only for the services of an investment manager. A
 * question with its own condition keeps it, and both must hold.
 */
export function onlyForManagedServices(
    question: ClientApplicationQuestion,
): ClientApplicationQuestion {
    const own = question.showWhen;
    if (own === undefined) return { ...question, showWhen: MANAGED_SERVICES };
    const list: readonly ClientApplicationCondition[] = Array.isArray(own)
        ? (own as readonly ClientApplicationCondition[])
        : [own as ClientApplicationCondition];
    return { ...question, showWhen: [MANAGED_SERVICES, ...list] };
}

const NATIONAL_CLIENT_IDENTIFIER_EXPLAINER = entityNationalClientIdentifierExplainer(
    'underlying parties connected to the Trust',
);

// ---------------------------------------------------------------------------
// Part 1, the trust details
// ---------------------------------------------------------------------------

/** The box about the representative and the services, page 1 of the form. */
const TRUST_DETAILS_NOTES: readonly ClientApplicationStatement[] = [
    {
        id: 'trust_representatives_note_1',
        text: 'Nominated Representative (NR): a nominated representative can be: a trustee who has been nominated by their co-trustees to make investment decisions on behalf of the Trust; or a charitable trust under which the directors/trustees have nominated an individual to make investment decisions on behalf of the Trust.',
    },
    {
        id: 'trust_representatives_note_2',
        text: 'The nominated representative is the individual whom we assess with regards to suitability, specifically in relation to investment knowledge and experience. They will also be responsible for receiving our correspondence and recommendations. Depending on their level of knowledge and experience we may alter what we determine is ultimately suitable as a portfolio structure. This will be detailed in the account opening letter.',
    },
    {
        id: 'trust_service_note_discretionary',
        text: 'Discretionary Managed service: A professional investment manager makes investment decisions for the Trust without needing its approval each time.',
    },
    {
        id: 'trust_service_note_advisory',
        text: 'Advisory service: The Trust gets advice from a professional investment manager, but the trust makes the final decision on its investments and execute trades itself.',
    },
    {
        id: 'trust_service_note_execution_only',
        text: 'Execution only service: Dowgate Wealth is only responsible for executing the trades as requested by the Trust. We will not offer any investment advice or recommendations.',
    },
];

const TRUST_DETAILS_SECTION: ClientApplicationSection = {
    id: 'trust_details',
    part: 1,
    title: 'Trust details',
    stepLabel: 'Trust',
    notes: TRUST_DETAILS_NOTES,
    questions: [
        { id: 'trust_name', kind: 'short_text', label: 'Full name of Trust', required: true },
        {
            id: 'trust_type',
            kind: 'single_select',
            label: 'Type (bare trust, discretionary trust etc)',
            required: true,
            options: TRUST_TYPE_OPTIONS,
        },
        {
            id: 'trust_type_other',
            kind: 'short_text',
            label: 'Other (please specify)',
            required: true,
            showWhen: { questionId: 'trust_type', equals: ['Other'] },
        },
        {
            // The charity number is not on the paper form. The entity fact
            // find holds it (open question Q19).
            id: 'trust_charity_number',
            kind: 'short_text',
            label: 'Charity number',
            showWhen: CHARITY,
        },
        { id: 'trust_date_established', kind: 'date', label: 'Date established', required: true },
        {
            id: 'trust_country_of_establishment',
            kind: 'country_select',
            label: 'Country of establishment',
            required: true,
        },
        {
            // "*not required for Bare Trusts": a bare trust does not see it.
            id: 'trust_lei_number',
            kind: 'short_text',
            label: 'Legal Entity Identifier (LEI)',
            explainer: 'Not required for Bare Trusts.',
            required: true,
            showWhen: {
                questionId: 'trust_type',
                equals: TRUST_TYPE_OPTIONS.filter((type) => type !== 'Bare trust'),
            },
        },
        {
            id: 'trust_purpose',
            kind: 'long_text',
            label: 'Nature/Purpose and objectives of the Trust',
            required: true,
        },
        ...addressQuestions({
            prefix: 'trust_correspondence_address',
            group: 'Correspondence address',
            required: true,
            houseLabel: 'Building no./name',
        }),
        {
            id: 'trust_phone_number',
            kind: 'short_text',
            label: 'Phone number',
            textFormat: 'phone',
            group: 'Correspondence address',
        },
        {
            id: 'trust_email',
            kind: 'short_text',
            label: 'Email',
            textFormat: 'email',
            group: 'Correspondence address',
        },
        {
            // The paper form prints "REGISTERED ADDRESS (if different to
            // Correspondence Address)" and no question. The online form asks.
            id: 'trust_registered_address_different',
            kind: 'yes_no',
            label: 'Is the registered address different to the correspondence address?',
            required: true,
            group: 'Registered address',
        },
        ...addressQuestions({
            prefix: 'trust_registered_address',
            group: 'Registered address',
            required: true,
            houseLabel: 'Building no./name',
            showWhen: { questionId: 'trust_registered_address_different', equals: ['Yes'] },
        }),
    ],
};

// ---------------------------------------------------------------------------
// Parts 2 and 3, the persons
// ---------------------------------------------------------------------------

/** The capacity question of Parts 2 and 3. */
const CAPACITY_LABEL =
    'Please detail your capacity as a representative of the Trust eg Settlor, Beneficiary, Trustee or Protector';

const APPLICANT_SECTION: ClientApplicationSection = {
    id: 'applicant',
    part: 2,
    title: 'Nominated representative - personal information',
    stepLabel: 'Representative',
    intro: 'This section is to be completed by the Nominated Representative who will be the main contact for the account and will make investment decisions on behalf of the Trust - see page 1 for more information.',
    questions: entityApplicantQuestions({
        capacityLabel: CAPACITY_LABEL,
        nationalClientIdentifierExplainer: NATIONAL_CLIENT_IDENTIFIER_EXPLAINER,
    }),
};

const PARTIES_SECTION: ClientApplicationSection = {
    id: 'trust_parties',
    part: 3,
    title: 'Personal information',
    stepLabel: 'Parties',
    intro: 'This section is to be completed by all underlying parties connected to the Trust such as the Settlor, Beneficiaries and Trustees; and any individual who is entitled to a specified interest in at least 25% of the capital of the trust property. If needed, please use the Additional Information section at the back of the form to add further details.',
    questions: personGroup({
        prefix: TRUST_PARTY_PREFIX,
        count: TRUST_PARTY_COUNT,
        heading: (n) => `Personal information (${n})`,
        openerLabel: 'Does the Trust have another connected person to add?',
        capacityLabel: CAPACITY_LABEL,
        phones: 'one',
        nationalClientIdentifierExplainer: NATIONAL_CLIENT_IDENTIFIER_EXPLAINER,
    }),
};

// ---------------------------------------------------------------------------
// Part 4, the financial status of the trust (managed services)
// ---------------------------------------------------------------------------

function money(id: string, label: string, group: string): ClientApplicationQuestion {
    return { id, kind: 'currency', label, group };
}

function text(id: string, label: string, group: string): ClientApplicationQuestion {
    return { id, kind: 'short_text', label, group };
}

/** A row that only a charity sees (open question Q19). The paper form does not print it. */
function charity(question: ClientApplicationQuestion): ClientApplicationQuestion {
    return { ...question, showWhen: CHARITY };
}

const FINANCIAL_STATUS_SECTION: ClientApplicationSection = {
    id: 'trust_financial_status',
    part: 4,
    title: 'Financial status of the Trust',
    stepLabel: 'Finances',
    intro: 'This section is to be completed by the Nominated Representative who will be making decisions on behalf of the Trust. This section does not need to be completed if you have chosen an Execution Only account.',
    questions: [
        money('trust_income_investment', 'Investment income', 'Annual income'),
        money('trust_income_all_other', 'All other income', 'Annual income'),
        text('trust_income_all_other_sources', 'All other income source(s)', 'Annual income'),
        charity(
            money(
                'trust_income_donations_grants',
                'Donations, grants and legacies',
                'Annual income',
            ),
        ),
        money(
            'trust_commitment_loan_repayments',
            'Loan repayments',
            'Annual financial commitments',
        ),
        money(
            'trust_commitment_other_significant_expenses',
            'Other significant expenses',
            'Annual financial commitments',
        ),
        text(
            'trust_commitment_other_significant_expenses_detail',
            'Other significant expenses detail',
            'Annual financial commitments',
        ),
        charity(
            money(
                'trust_commitment_distributions',
                'Grants and distributions paid',
                'Annual financial commitments',
            ),
        ),
        charity(
            money(
                'trust_commitment_administrative_expenses',
                'Administrative expenses',
                'Annual financial commitments',
            ),
        ),
        money('trust_asset_property_value', 'Trust property value', 'Assets'),
        money('trust_asset_cash_deposits', 'Trust cash deposits', 'Assets'),
        money('trust_asset_other_investments_value', 'Value of other investments', 'Assets'),
        money('trust_asset_other', 'Other assets', 'Assets'),
        text('trust_asset_other_detail', 'Other assets detail', 'Assets'),
        money('trust_liability_tax', 'Tax', 'Liabilities'),
        money('trust_liability_mortgage_outstanding', 'Mortgage outstanding', 'Liabilities'),
        money('trust_liability_other_debt', 'Other debt', 'Liabilities'),
        text('trust_liability_other_debt_detail', 'Other debt detail', 'Liabilities'),
    ].map(onlyForManagedServices),
};

// ---------------------------------------------------------------------------
// Parts 5 to 7
// ---------------------------------------------------------------------------

const HEALTH_SECTION = entityHealthSection(
    5,
    'This section is to be completed by the Nominated Representative who will be making decisions on behalf of the Trust.',
);

/** One row of the source table: the row key, the option label and the prompt of the form. */
type SourceRow = readonly [key: string, label: string, prompt: string];

/** The prompt of each row of the source table, in the words of the trust form. */
const SOURCE_PROMPTS: readonly SourceRow[] = [
    [
        'employment',
        'Employment',
        'Please provide details of individual e.g. Settlor, their occupation and the nature of the business.',
    ],
    [
        'savings_investments',
        'Savings & Investments',
        'Please provide details of how these were accumulated and by whom.',
    ],
    [
        'inheritance',
        'Inheritance',
        'Please provide the full name of the deceased, relationship to the Trust and their primary source of wealth.',
    ],
    [
        'gift',
        'Gift',
        'Please provide the full name of the donor, their relationship to the Trust and their primary source of wealth.',
    ],
    [
        'property',
        'Property',
        'Please provide details of the property, including its address and date sold if relevant.',
    ],
    [
        'business',
        'Business',
        'Please provide details of the business including the name of the business, the nature of its activities and connection to the Trust.',
    ],
    ['other', 'Other', 'If Source of Wealth is ‘other’, please provide further details.'],
];

/**
 * The source table of the individual form, with the prompts of an entity form.
 * The option labels stay the labels of the individual form (rule H6): the
 * trust form prints "Employment income", and the stored answer is
 * "Employment".
 */
export function entitySourceOfWealthQuestions(
    rows: readonly SourceRow[],
): ClientApplicationQuestion[] {
    const changes = questionChanges([
        [
            'source_types',
            { optionNotes: Object.fromEntries(rows.map(([, label, prompt]) => [label, prompt])) },
        ],
        ...rows.flatMap(([key, , prompt]) => [
            [`source_of_wealth_${key}_detail`, { explainer: prompt }] as const,
            [`source_of_funds_${key}_detail`, { explainer: prompt }] as const,
        ]),
    ]);
    return individualSectionQuestions(
        'source_of_wealth_and_funds',
        '3.0',
        [
            'source_types',
            ...rows.flatMap(([key]) => [
                `source_of_wealth_${key}_detail`,
                `source_of_funds_${key}_detail`,
            ]),
        ],
        changes,
    );
}

const SOURCE_OF_WEALTH_SECTION: ClientApplicationSection = {
    id: 'source_of_wealth_and_funds',
    part: 6,
    title: 'Source of wealth & funds',
    stepLabel: 'Wealth & funds',
    intro: 'In very simple terms, source of wealth is about how the Trust earned its wealth over time, while source of funds is about where the money it will be using to invest comes from. We need to understand both of these elements for a clearer picture of the Trust’s financial situation, so we can manage its investments and service its needs in the best way. These requirements are also part of the UK’s broader anti-money laundering regulations and help ensure the integrity of the financial system.',
    questions: entitySourceOfWealthQuestions(SOURCE_PROMPTS),
};

const ACCOUNT_USAGE_SECTION: ClientApplicationSection = {
    id: 'account_type_and_usage',
    part: 7,
    title: 'Account usage',
    stepLabel: 'Account usage',
    intro: 'It’s important for us to understand the Trust’s expectations regarding withdrawals from its portfolio, so we can plan the Trust’s investments accordingly. It’s also a regulatory requirement and additional protection for the Trust if, for example, we observe any unusual activity. This section is to be completed by the Nominated Representative.',
    questions: entityAccountUsageQuestions({
        regular: 'For example, dividends paid out monthly or quarterly to the Trust bank account',
        capital: 'For example, capital is required to invest in a new property or care provision',
        funding:
            'For example, transfer of funds from the Settlor to the Trust’s investment account',
    }),
};

// ---------------------------------------------------------------------------
// Parts 8 and 9 (managed services)
// ---------------------------------------------------------------------------

const INVESTMENT_OBJECTIVES_SECTION: ClientApplicationSection = {
    id: 'investment_objectives_and_experience',
    part: 8,
    title: 'Investment objectives & experience',
    stepLabel: 'Objectives',
    intro: 'It’s important for us to understand the Trust’s objectives and any previous investment experience. Not only is this a UK regulatory requirement. It also helps ensure the portfolio we build for the Trust is suitable, and the advice and way we communicate with the Trust is appropriate. This section is to be completed by the Nominated Representative. As main account contact and decision maker, the nominated representative is the individual whom we assess with regards to investment experience. This section does not need to be completed if you have chosen an Execution Only account.',
    notes: [
        {
            id: 'trust_client_classification_note_1',
            text: 'To provide the best possible services and outcomes we need to assign a client classification to every client - which are investor categories defined by the financial regulator. This classification helps us understand the Trust’s financial knowledge and needs, ensuring we offer the right products and advice. It also ensures that we follow the necessary regulations to protect the Trust based on your experience and the amount of money being invested.',
        },
        {
            id: 'trust_client_classification_note_2',
            text: 'Please note, we will classify the Trust as a retail client unless you strongly feel a professional classification is more appropriate. Please see the descriptions below and inform your Trust’s Investment Manager if you feel you fall outside of the retail client classification.',
        },
        {
            id: 'trust_client_classification_note_3',
            text: 'Retail client: Generally small Trusts which have limited scope for financial services, typically with less experience or knowledge of investing.',
        },
        {
            id: 'trust_client_classification_note_4',
            text: 'Professional client: A Trust client with a higher level of investment knowledge and experience, and whose career may have been in, or related to, financial markets.',
        },
    ],
    questions: entityObjectivesQuestions({
        objectiveLabel: 'What best describes the investment objective of the Trust?',
        restrictionsLabel:
            'If the Trust has any ethical, environmental, geographic, sector, or stock specific restrictions, please provide these in as much detail as possible below:',
    }).map(onlyForManagedServices),
};

/** The twelve risk questions of the individual form 2.0, with the words of the trust form. */
const RISK_LABELS: ReadonlyArray<readonly [string, string]> = [
    [
        'risk_protection_over_returns',
        'With regards to this investment, I generally consider protecting the Trust’s investment is more important than the potential to achieve high returns:',
    ],
    [
        'risk_prefer_fewer_ups_and_downs',
        'With regards to this investment, the Trust would prefer fewer ups and downs in value, and would be willing to accept the lower returns this investment may make:',
    ],
    [
        'risk_comfortable_with_smaller_companies',
        'Smaller companies can achieve faster and higher growth but can also be illiquid and at greater risk of failure than FTSE 100 companies. The Trust would be comfortable to invest in this type of company for the potential to achieve a larger return:',
    ],
    [
        'risk_understands_smaller_company_risks',
        'I understand – and am comfortable with – the risks associated with investing in smaller companies:',
    ],
    [
        'risk_response_to_quarter_fall',
        'In the four weeks before the first lockdown (23 March 2020), the FTSE 100 fell 25% in value. If the Trust owned an investment that lost over 25% in four weeks, it would:',
    ],
    [
        'risk_loss_that_prompts_move_to_cash',
        'Over the short term, investments are likely to fall as well as rise. The Trust would begin to feel the need to cut its losses and move to cash upon a loss of:',
    ],
    [
        'risk_investment_horizon',
        'How long do you think the Trust will remain invested before taking its money out?',
    ],
    ['risk_income_security', 'How secure are the Trust’s sources of income?'],
    [
        'risk_income_change_over_ten_years',
        'Over the course of the next ten years, how do you expect the Trust’s level of income to change, if at all?',
    ],
    [
        'risk_liquid_funds_months_of_expenses',
        'How many months of expenses could the Trust’s liquid funds cover?',
    ],
    [
        'risk_capacity_for_loss',
        'How much of the Trust’s investments with Dowgate Wealth do you think that it is able to lose without having a significant impact on its future commitments or investment objectives?',
    ],
    [
        'risk_liabilities_change_over_ten_years',
        'Over the course of the next ten years, how do you expect the Trust’s liabilities to change, if at all?',
    ],
];

const RISK_SECTION: ClientApplicationSection = {
    id: 'risk_tolerance_and_capacity_for_loss',
    part: 9,
    title: 'Risk tolerance & capacity for loss',
    stepLabel: 'Risk',
    intro: 'This section should be completed by the Nominated Representative. It does not need to be completed if you have chosen an Execution Only account. Whether it’s your first investment responsibility or you’re an experienced investor, grasping the relationship between risk and return is fundamental to making informed decisions. The questions in this section help us evaluate the Trust’s tolerance and capacity for risk, ensuring we apply the appropriate risk profile to the portfolio. You can find out more in our Risk and Investment guide and Capacity for Loss guide.',
    questions: entityRiskQuestions(RISK_LABELS).map(onlyForManagedServices),
};

// ---------------------------------------------------------------------------
// Parts 10 to 14
// ---------------------------------------------------------------------------

const BANK_ACCOUNT_SECTION: ClientApplicationSection = {
    id: 'bank_account_information',
    part: 10,
    title: 'Bank account information',
    stepLabel: 'Bank account',
    intro: 'Please note that payments will only be made to and accepted from the Trust’s nominated bank account detailed below and that a certified copy of a bank statement will be required. For non-UK bank accounts, please provide IBAN, BIC / ABA numbers and the account currency.',
    questions: entityBankAccountQuestions(),
};

const THIRD_PARTY_SECTION: ClientApplicationSection = {
    id: 'third_party_disclosure',
    part: 11,
    title: 'Third party information',
    stepLabel: 'Third party',
    intro: 'In certain situations, it is useful to have copies of certain reports and documentation relating to the Trust’s account(s) sent to third parties. For example a tax advisor for assessment and tax filing purposes. If you would like Dowgate Wealth to provide information regarding the account to a third-party individual, please provide the relevant details below.',
    questions: entityThirdPartyQuestions(),
};

const PORTAL_SECURITY_SECTION: ClientApplicationSection = {
    id: 'online_portal_security_questions',
    part: 12,
    title: 'Online portal security questions',
    stepLabel: 'Portal security',
    intro: 'Our online portal gives you access to the Trust’s investment portfolio, so you can see its holdings, the value of investments and a history of all transactions. For security, we ask the Nominated Representative to provide answers to the following questions. Additional access can be provided upon request for other Trustees or third parties.',
    questions: entityPortalSecurityQuestions(),
};

const AUTHORISED_SIGNATORIES_SECTION: ClientApplicationSection = {
    id: 'authorised_signatories',
    part: 13,
    title: 'Authorised signatories',
    stepLabel: 'Signatories',
    intro: 'Please provide details and signatures of all Authorised Signatories for the investment portfolio including the individual acting as the Nominated Representative.',
    questions: authorisedSignatoryQuestions(TRUST_AUTHORISED_SIGNATORY_COUNT),
};

const ADDITIONAL_NOTES_SECTION = entityAdditionalNotesSection(
    'If you need to add more information – for example additional trustee information or additional third-party information requirements – please use the additional notes section at the end of this form.',
);

/** The statements of the trust declarations. Declaration 6c is not here (rule H8). */
const TRUST_DECLARATIONS: readonly ClientApplicationStatement[] = [
    {
        id: 'trust_declaration_1',
        text: 'I have read and understood the nature and the risk of the product(s) that the Trust intends to invest in through this account.',
    },
    {
        id: 'trust_declaration_2',
        text: 'I act on behalf of the Trust as specified on this application form and not on behalf of another party in respect of all matters related to this account. Accordingly, all funds to be deposited and transacted on the account are the funds of the Trust.',
    },
    {
        id: 'trust_declaration_3',
        text: 'I am not aware of any health or other conditions that I may have that might affect the proper operation of the account on behalf of the Trust.',
    },
    {
        id: 'trust_declaration_4',
        text: 'I have provided true, accurate and complete information and undertake to update Dowgate Wealth of any changes to the information provided without delay.',
    },
    {
        id: 'trust_declaration_5',
        text: 'I accept that in certain circumstances Dowgate Wealth will be obliged to share information with UK tax authorities, who may pass it on to other tax authorities.',
    },
    { id: 'trust_declaration_6', text: 'I have received, read and understood the following:' },
    {
        id: 'trust_declaration_6a',
        text: 'a. Dowgate Wealth Terms of Business - including the product risks disclosure (website link: https://dowgatewealth.co.uk/terms-of-business/)',
    },
    {
        id: 'trust_declaration_6b',
        text: 'b. Conflict of Interest Policy (website link: https://dowgatewealth.co.uk/conflicts-of-interest-policy/)',
    },
    // Rule H8: "c. Schedule of Charges" is removed on purpose, as on the
    // individual form (SEP-263.39). The lettering stays a, b, d.
    {
        id: 'trust_declaration_6d',
        text: 'd. I have obtained the Dowgate Wealth Order Execution Policy (website link: https://dowgatewealth.co.uk/dowgate-wealth-ltd-order-execution-policy/)',
    },
    {
        id: 'trust_declaration_7',
        text: 'I consent to the Order Execution Policy and for any orders to be executed outside a regulated market or a multilateral trading facility.',
    },
    {
        id: 'trust_declaration_8',
        text: 'I consent for any unexecuted limit orders not to be made public.',
    },
    {
        id: 'trust_declaration_9',
        text: 'I accept and agree to be bound by the terms provided above and consent to such terms and information including future updates to these be provided to me by way of posting on the websites indicated above.',
    },
];

const DECLARATIONS_SECTION = entityDeclarationsSection(
    14,
    entityDeclarationQuestions({
        statements: TRUST_DECLARATIONS,
        nominatedRepresentative: {
            explainer:
                'The nominated representative and the other trustees of the Trust must also sign. We will contact you to arrange these signatures.',
        },
        marketingExplainer:
            'We offer company news and market commentary which is emailed to the Nominated Representative detailed in Section 2. If you would like to receive this, please tick this box:',
    }),
);

// ---------------------------------------------------------------------------
// The bank
// ---------------------------------------------------------------------------

/** The bank of the trust form, `DGW Trust App Form v1.1`. */
export const TRUST_BANK: ClientApplicationBank = {
    versions: TRUST_FORM_VERSIONS,
    sections: [
        sharedServiceTypeSection(),
        TRUST_DETAILS_SECTION,
        APPLICANT_SECTION,
        PARTIES_SECTION,
        FINANCIAL_STATUS_SECTION,
        HEALTH_SECTION,
        SOURCE_OF_WEALTH_SECTION,
        ACCOUNT_USAGE_SECTION,
        INVESTMENT_OBJECTIVES_SECTION,
        RISK_SECTION,
        BANK_ACCOUNT_SECTION,
        THIRD_PARTY_SECTION,
        PORTAL_SECURITY_SECTION,
        AUTHORISED_SIGNATORIES_SECTION,
        ADDITIONAL_NOTES_SECTION,
        DECLARATIONS_SECTION,
    ],
    ...ENTITY_BANK_RULES,
};
